import { Preferences } from "@capacitor/preferences";
import {
  IonAvatar,
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonPage,
  IonRow,
  IonToast,
} from "@ionic/react";
import * as Sentry from "@sentry/react";
import {
  alertCircleOutline,
  arrowForwardOutline,
  qrCodeOutline,
} from "ionicons/icons";
import { useState } from "react";
import { useParams } from "react-router";
import { getVehicleById } from "../../api/vehicles";
import backgroundTrailerImage from "../../assets/image/backgroundTrailer.png";
import TrailerLeft from "../../assets/svg/trailerLeft.svg";
import Breadcrumb from "../../components/Breadcrumb";
import { useToast } from "../../components/ToastContext";
import { ApiError } from "../../types/api/api.types";
import { Vehicle } from "../../types/api/vehicle.types";
import { BarcodeScanError, runBarcodeScan } from "../../utils/barcode";
import "./IdentificationTrailer.css";

const IdentificationTrailer: React.FC = () => {
  const { showToast } = useToast();

  const { vehicleId } = useParams<{ vehicleId: string }>();
  const [scanResult, setScanResult] = useState<any | null>();
  const [toastAdviceIsOpen, setToastAdviceIsOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const scanBarcode = async () => {
    try {
      const code = await runBarcodeScan();
      const response = await getVehicleById(code);

      if (response && (response as ApiError).error) {
        return showToast(`${(response as ApiError).error.message}`);
      }

      if (!(response as Vehicle).active) {
        setToastMessage("Véhicule invalide");
        setToastAdviceIsOpen(true);
      } else if ((response as Vehicle).type.code !== "Trailer") {
        setToastMessage(
          "Vous avez scanné un véhicule, veuillez scanner un semi-remorque",
        );
        setToastAdviceIsOpen(true);
      } else if ((response as Vehicle).immobilized) {
        setToastMessage("Semi-remorque immobilisé pour réparation");
        setToastAdviceIsOpen(true);
      } else {
        setScanResult(response as Vehicle);
      }
    } catch (error) {
      if (error instanceof BarcodeScanError) {
        showToast(error.message);
      } else {
        Sentry.captureException(error);
        showToast("Une erreur inattendue est survenue. Veuillez réessayer.");
      }
    }
  };

  const setJourney = async () => {
    let currentJourney =
      JSON.parse(
        (await Preferences.get({ key: "journey" }))?.value as string,
      ) || [];

    await Preferences.set({
      key: "journey",
      value: JSON.stringify({
        ...currentJourney,
        trailerId: scanResult.id,
      }),
    });
  };

  return (
    <IonPage>
      <IonContent fullscreen className="IdentificationTrailer">
        <IonToast
          isOpen={toastAdviceIsOpen}
          message={toastMessage}
          onDidDismiss={() => setToastAdviceIsOpen(false)}
          position="top"
          duration={20000}
          icon={alertCircleOutline}
          className="IdentificationVehicle-toast"
          buttons={[
            {
              text: "Fermer",
              role: "cancel",
            },
          ]}
        ></IonToast>
        <Breadcrumb
          backUrl={`/inspection/${vehicleId}/Tractor/Back`}
          activeElement={2}
          trailer={true}
        />
        <IonRow className="IdentificationTrailer-title">
          Identification Semi-remorque
        </IonRow>
        <div className="IdentificationTrailer-description">
          Vos devez maintenant
          <span className="IdentificationTrailer-description_highlight">
            {" "}
            scanner le QR code
          </span>{" "}
          qui se trouve sur le côté droit de la semi-remorque
        </div>
        <IonButton
          color="success"
          onClick={async () => scanBarcode()}
          expand="block"
          className="IdentificationTrailer-button-item"
        >
          <div className="IdentificationTrailer-button">
            <div className="IdentificationTrailer-button-content">
              <div className="IdentificationTrailer-button-logo">
                <IonIcon
                  icon={qrCodeOutline}
                  className="IdentificationTrailer-button-logo-icon"
                />
              </div>
              <div className="IdentificationTrailer-button-content-text">
                Scanner le QR code
              </div>
            </div>
            <IonIcon
              icon={arrowForwardOutline}
              className="IdentificationTrailer-button-icon"
            />
          </div>
        </IonButton>
        <IonButton
          color="success"
          fill="outline"
          routerLink={`/inspection/${vehicleId}/finishjourney`}
          expand="block"
          className="IdentificationTrailer-button-item"
        >
          Je n'ai pas de semi-remorque
        </IonButton>
        {scanResult ? (
          <IonButton
            color="warning"
            routerLink={`/inspection/${scanResult.id}/Trailer/Document`}
            routerDirection="forward"
            expand="block"
            className="IdentificationTrailer-button-item"
            onClick={async () => {
              await setJourney();
            }}
          >
            <div className="IdentificationVehicle-button">
              <div className="IdentificationVehicle-button-content">
                <IonAvatar className="IdentificationVehicle-button-content-avatar">
                  <IonIcon
                    src={TrailerLeft}
                    className="IdentificationVehicle-button-content-avatar-icon"
                  />
                </IonAvatar>
                <div className="IdentificationVehicle-button-content-text">
                  {scanResult.brand}
                  <br />
                  {scanResult.registrationNumber}
                  <br />
                  {scanResult.internalCode}
                </div>
              </div>
              <IonIcon
                icon={arrowForwardOutline}
                className="IdentificationVehicle-button-icon"
              />
            </div>
          </IonButton>
        ) : null}
        <div className="IdentificationTrailer-footer">
          <IonImg
            src={backgroundTrailerImage}
            alt="Background image"
            className="IdentificationTrailer-footer-image"
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default IdentificationTrailer;
